import { useNavigate, useParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { useHospitalTitle } from '../../lib/hospital-brand';
import { useNurseryChild } from './nursery.queries';

const printableDate = (value: string | null, withTime = true) => value
  ? new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: true } : {}),
      timeZone: 'Asia/Kolkata',
    }).format(new Date(value))
  : '-';

const ageText = (dateOfBirth: string | null) => {
  if (!dateOfBirth) return '-';
  const days = Math.max(0, Math.floor((Date.now() - new Date(dateOfBirth).getTime()) / 86_400_000));
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  return `${years} YEARS ${months} MONTHS ${days % 30} DAYS`;
};

export const NurseryCertificatePage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const hospitalTitle = useHospitalTitle();
  const childQuery = useNurseryChild(childId);

  if (childQuery.isLoading) return <LoadingState />;
  if (childQuery.isError || !childQuery.data) return <ErrorState title="Unable to load certificate" message={childQuery.error instanceof ApiError ? childQuery.error.message : 'Birth certificate data could not be loaded.'} action={{ label: 'Back to Nursery', onClick: () => void navigate('/nursery') }} />;

  const child = childQuery.data;
  const rows = [
    ['Name Of The Patient', child.name],
    ['Age', ageText(child.dateOfBirth)],
    ["Husband's/Guardian's Name", child.guardianName || '-'],
    ['Address', [child.address, child.district, child.state, child.pinCode].filter(Boolean).join(', ')],
    ['Diagnosis', child.diagnosis || '-'],
    ['Operation', child.operation || '-'],
    ['Date & Time Of Birth Baby', printableDate(child.dateOfBirth)],
    ['Sex', child.gender],
    ['Wt.', child.weight || '-'],
    ['Mode Of Delivery', child.deliveryMode || '-'],
    ['Date Of Admission', printableDate(child.admissionDate)],
    ['Date Of Discharge', printableDate(child.dischargeDate)],
    ['Remarks', 'Both Mother and Baby Doing Well'],
  ];

  return (
    <div className="nursery-certificate-page">
      <div className="nursery-certificate-actions">
        <button className="button button-primary" type="button" onClick={() => window.print()}>Print</button>
        <button className="button button-secondary" type="button" onClick={() => void navigate('/nursery')}>Close</button>
      </div>
      <article className="nursery-certificate">
        <header><p>{hospitalTitle}</p><h1>Birth Certificate</h1><small>Baby ID: {child.babyId}</small></header>
        <div className="nursery-certificate-grid">
          {rows.map(([label, value]) => <div key={label}><strong>{label}</strong><span>{value}</span></div>)}
        </div>
        <div className="nursery-certificate-signature">
          <span>Date: {printableDate(new Date().toISOString(), false)}</span><span>Signature</span>
        </div>
        <footer>
          <strong>Please register birth at Kalyani Municipality within 15 Days (birth registration is mandatory)</strong>
          <span>This is a provisional certificate</span>
        </footer>
      </article>
    </div>
  );
};
